/* eslint-disable complexity */
import {ItemType, QuestionType} from 'common/constants/survey';
import {
    DropdownQuestion,
    Question,
    QuestionValue,
    Series,
    SeriesValue,
    SurveyPageItem,
    SurveySettings,
    SurveyValue,
    SurveyValues,
} from 'common/models/survey';
import {isNil} from 'lodash';
import * as Metrika from 'ui/utils/metrika';

import getValidQuestionValue from './getValidQuestionValue';
import isSeries from './isSeries';

export const getDefaultValue = (question: Question): QuestionValue => {
    switch (question.type) {
        case QuestionType.Dropdown:
            if (
                (question as DropdownQuestion).show_first &&
                (question as DropdownQuestion).items[0]
            ) {
                return [(question as DropdownQuestion).items[0].id];
            }
            return [];
        case QuestionType.Payment:
        case QuestionType.Integer:
            return '';
        case QuestionType.Boolean:
            return false;
        case QuestionType.Radio:
        case QuestionType.Stars:
        case QuestionType.OneRow:
        case QuestionType.Suggest:
        case QuestionType.Matrix:
        case QuestionType.File:
        case QuestionType.Checkbox:
            return [];
        case QuestionType.Captcha:
            return {key: '', value: ''};
        case QuestionType.String:
        default:
            return '';
    }
};

const getDefaultSurveyValue = (item: SurveyPageItem) => {
    if (isSeries(item)) {
        const questions: SeriesValue = {};
        item.items.forEach((question) => {
            questions[question.id] = getDefaultValue(question);
        });
        return [questions];
    }
    return getDefaultValue(item);
};

const getValueFromSearchParams = (
    item: SurveyPageItem,
    searchParamsValues?: Record<string, QuestionValue | QuestionValue[]>,
) => {
    if (!searchParamsValues || isNil(item)) {
        return undefined;
    }
    if (isSeries(item)) {
        const values: SeriesValue[] = [];
        item.items.forEach((question) => {
            const valueArray = searchParamsValues[question.id];
            if (valueArray && Array.isArray(valueArray)) {
                (valueArray as QuestionValue[]).forEach((value, index) => {
                    if (!values[index]) {
                        values[index] = {};
                    }
                    const validValue = getValidQuestionValue(question, value);
                    if (validValue !== null) {
                        values[index][question.id] = validValue;
                    }
                });
            }
        });
        return values.length > 0 ? values : undefined;
    }
    return getValidQuestionValue(item, searchParamsValues[item.id] as QuestionValue);
};

const isSeriesValueArray = (value: SurveyValue, item: SurveyPageItem): value is SeriesValue[] => {
    return item.type === ItemType.Series && Array.isArray(value);
};

const getFixedValue = (item: SurveyPageItem, value?: SurveyValue): SurveyValue | null => {
    if (!value) {
        return null;
    }

    if (isSeries(item)) {
        if (!isSeriesValueArray(value, item)) {
            return [];
        }
        const questionMap = (item as Series).items.reduce<Record<string, Question>>(
            (acc, question) => {
                acc[question.id] = question;
                return acc;
            },
            {},
        );

        const checkedValue: SeriesValue[] = [];
        value.forEach((seriesValue) => {
            const valueSet: Record<string, QuestionValue> = {};
            for (const [key, defaultValue] of Object.entries(seriesValue)) {
                const fixedValue =
                    questionMap[key] && getValidQuestionValue(questionMap[key], defaultValue);
                if (!isNil(fixedValue)) {
                    valueSet[key] = fixedValue;
                }
            }
            checkedValue.push(valueSet);
        });

        return checkedValue;
    }

    return getValidQuestionValue(item, value as QuestionValue);
};

const isNotNilOrEmptyString = (value?: SurveyValue | null): value is SurveyValue => {
    return !isNil(value) && value !== '';
};

// Priority: 1. searchParamsValues 2. localStorageValues
// 3. defaultValueFromBackend 4. defaultValue
const getDefaultValues = (
    survey?: SurveySettings,
    searchParamsValues?: Record<string, QuestionValue | QuestionValue[]>,
    localStorageValues?: SurveyValues,
): SurveyValues => {
    const result: SurveyValues = {};
    if (!survey || !Array.isArray(survey.pages)) {
        return result;
    }
    survey?.pages.forEach((page) => {
        page.items.forEach((item) => {
            if (isNil(item)) {
                return;
            }
            const searchParamsValue = getValueFromSearchParams(item, searchParamsValues);
            const localStorageValue = getFixedValue(item, localStorageValues?.[item.id]);
            const valueFromBackend = getFixedValue(item, survey.values?.[item.id]);

            let value;
            if (isNotNilOrEmptyString(searchParamsValue)) {
                value = searchParamsValue;
                Metrika.defaultValues(Metrika.DefaultValueType.Query, item.type);
            } else if (isNotNilOrEmptyString(localStorageValue) && !(item as Question).hidden) {
                value = localStorageValue;
                Metrika.defaultValues(Metrika.DefaultValueType.Storage, item.type);
            } else if (isNotNilOrEmptyString(valueFromBackend)) {
                value = valueFromBackend;
                Metrika.defaultValues(Metrika.DefaultValueType.Backend, item.type);
            } else {
                value = getDefaultSurveyValue(item);
            }

            result[item.id] = value;
        });
    });
    return result;
};
export default getDefaultValues;
